/**
 * Owner approval of a headless device's enrollment request, from a browser.
 *
 * The mechanism is `packages/core/src/verification/device-enrollment.ts`; the
 * CLI door is `excellent-mcp device-enrollment approve`. This contract adds a
 * SECOND FRONT DOOR ONTO THE SAME LOCK — the route handler
 * (`excellent/server/routes/device-enrollment.ts`) runs the byte-identical
 * authority chain the CLI runs (current session → owner membership →
 * `action:mutate` → `decideDeviceEnrollmentApproval` →
 * `recordDeviceEnrollmentApproval`), so that an owner holding a phone can say
 * yes without shell access and a session file on disk.
 *
 * WHAT IS DELIBERATELY NOT HERE.
 *
 *  · No workspace parameter. The tenant is derived from the caller's own
 *    authenticated session, never from anything the caller sends. A request
 *    body that could name a workspace is a request body that could name
 *    SOMEONE ELSE'S workspace.
 *  · No device secret, and no `deviceSecretHash`. The approval surface never
 *    sees, transports or returns the redemption credential; approving is not
 *    redeeming, and an owner's browser is not the device.
 *  · No scope vocabulary. `scopes` is bounded free text here because the only
 *    check that matters is CONTAINMENT in what the device asked for, and that
 *    check lives in `decideDeviceEnrollmentApproval`. Restating an allowlist
 *    here would be a second, driftable copy of a decision that is already made
 *    once.
 *
 * The `userCode` IS returned, and must be: it is the string the owner matches
 * against the headless console before consenting. It is not a credential —
 * redemption additionally requires the device secret, which never leaves the
 * machine that generated it.
 */

import { z } from "zod";
import type { Operation } from "../types";

/** How a pending request reads to the human deciding on it. Every field is
 * public-by-construction: the label the operator typed, what was asked for,
 * when, and the code to match. Nothing here is usable to redeem. */
export const deviceEnrollmentRequestSchema = z.object({
  requestId: z.string(),
  userCode: z.string(),
  deviceLabel: z.string(),
  requestedScopes: z.array(z.string()),
  requestedAt: z.string(),
  expiresAt: z.string(),
  /** Derived, not stored — `pending` is the only state that can be approved. */
  state: z.enum(["pending", "approved", "redeemed", "expired"]),
  approvedAt: z.string().nullable(),
  approvedScopes: z.array(z.string()).nullable(),
  redeemedAt: z.string().nullable(),
});
export type DeviceEnrollmentRequestView = z.infer<typeof deviceEnrollmentRequestSchema>;

const requestsResponse = z.object({
  /** Echoed so the page can show WHICH workspace it is approving for — the
   * answer came from the session, so it is also the proof the page is not
   * quietly scoped somewhere the owner did not expect. */
  workspaceId: z.number(),
  enrollments: z.array(deviceEnrollmentRequestSchema),
});

const requestsOp: Operation<void, void, void, z.infer<typeof requestsResponse>> = {
  method: "GET",
  path: () => "/api/verification/device-enrollment/requests",
  response: requestsResponse,
};

const approveBody = z.object({
  /** As the human retypes it. Normalized (case, separators) by
   * `normalizeUserCode`; bounded here so a 10MB body never reaches it. */
  userCode: z.string().min(1).max(32),
  /** Optional NARROWING. Absent means "exactly what the device requested".
   * Widening is refused by `decideDeviceEnrollmentApproval`, not by this
   * schema — see the file header. */
  scopes: z.array(z.string().min(1).max(64)).min(1).max(35).optional(),
});

const approveResponse = z.object({
  requestId: z.string(),
  userCode: z.string(),
  deviceLabel: z.string(),
  approvedScopes: z.array(z.string()),
  approvedByActorId: z.string(),
  approvedAt: z.string(),
});

const approveOp: Operation<
  void,
  void,
  z.infer<typeof approveBody>,
  z.infer<typeof approveResponse>
> = {
  method: "POST",
  path: () => "/api/verification/device-enrollment/approve",
  body: approveBody,
  response: approveResponse,
  invalidates: [requestsOp],
};

export const deviceEnrollment = { requests: requestsOp, approve: approveOp };
